import { nullIfBlank } from "src/utils/strings.tsx";
import { Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { GridRenderCellParams, GridRowId, GridValidRowModel } from "@mui/x-data-grid-pro";
import { FlattenedAavoGridColDef } from "src/components/common/dataGrid/types.tsx";
import { SxProps, Theme } from "@mui/material/styles";

export interface FormattedColumnValueProps<RowData extends GridValidRowModel> {
	api: GridRenderCellParams["api"];
	rowId: GridRowId;
	column: FlattenedAavoGridColDef<RowData>;
	noTypographyWrapper?: boolean;
	typographySx?: SxProps<Theme>;
}

export const FormattedColumnValue = <RowData extends GridValidRowModel>({
	api,
	rowId,
	column,
	typographySx,
	noTypographyWrapper,
}: FormattedColumnValueProps<RowData>) => {
	// Row may have been removed from the grid (e.g. after data refresh) while a component
	// rendered outside the grid (such as the mobile details dialog) still references it.
	if (api.getRow(rowId) == null) return undefined;

	if (column.renderCell != null) {
		return column.renderCell({
			id: rowId,
			value: api.getCellValue(rowId, column.field),
			row: api.getRow(rowId),
			api: api,
		} as GridRenderCellParams); // Other input props should not be used
	}

	const cellParams = api.getCellParams(rowId, column.field);
	const formattedValue = cellParams.formattedValue;
	if (formattedValue == null) return undefined;
	if (typeof formattedValue === "string" && nullIfBlank(formattedValue) == null) return undefined;

	switch (typeof formattedValue) {
		case "string":
		case "number":
			if (noTypographyWrapper) {
				return formattedValue.toString();
			} else {
				return <Typography sx={typographySx}>{formattedValue.toString()}</Typography>;
			}
		case "boolean":
			return formattedValue ?
					<FontAwesomeIcon icon={faCheck} color={"green"} size={"lg"} />
				:	<FontAwesomeIcon icon={faXmark} color={"grey"} size={"lg"} />;
		default:
			return undefined;
	}
};
